<route>
{
  "meta": {
    "permission": [
      "equipements.view_equipement"
    ]
  }
}
</route>

<template>
  <v-container fluid>
    <v-row row wrap>
      <v-col cols="12" sm="3">
        <i-date-picker
          v-model="start"
          :label="$t('date_from')"
          color="primary"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <i-date-picker
          v-model="end"
          :label="$t('date_to')"
          color="primary"
          :min="start"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider vid="name" :name="$t('name')" v-slot="{ errors }">
          <v-text-field
            v-model="form.name"
            outlined
            :error-messages="errors[0]"
            :label="$t('name')"
            class="secondary--text"
          ></v-text-field>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider
          vid="production_department"
          :name="$t('department')"
          v-slot="{ errors }"
        >
          <v-select
            autocomplete="off"
            outlined
            :error-messages="errors[0]"
            v-model="form.production_department"
            item-value="pk"
            item-text="name"
            :items="departmentList"
            :label="$t('department')"
          ></v-select>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <ValidationProvider vid="model" :name="$t('model')" v-slot="{ errors }">
          <v-select
            autocomplete="off"
            outlined
            :error-messages="errors[0]"
            v-model="form.model"
            item-value="pk"
            item-text="modelName"
            :items="modelList"
            :label="$t('model')"
          ></v-select>
        </ValidationProvider>
      </v-col>
      <v-col cols="12" sm="3">
        <v-select
          outlined
          v-model="form.recurrent"
          :items="recurrent_options"
          item-text="name"
          item-value="pk"
          :label="$tc('recurrent', 1)"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="3">
        <v-btn color="secondary" @click="clear()">
          {{ $t('clear') }}
        </v-btn>
      </v-col>
    </v-row>
    <i-table
      :title="$t('equipmentReport')"
      :headers="headers"
      app="equipements.equipement"
      api="equipment"
      :opt="{
        name: this.form.name,
        production_department: this.form.production_department,
        model: this.form.model,
        date_from: this.start,
        date_to: this.end,
        recurrent: this.form.recurrent
      }"
      show-expand
      :expanded.sync="expanded"
      single-expand
      dontEdit
      dontCreate
      dontSearch
    >
      <template v-slot:item.data-table-expand="{ isExpanded, expand }">
        <v-btn
          icon
          @click="expand(!isExpanded)"
          :color="isExpanded ? 'primary' : ''"
        >
          <v-icon>{{
            isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
          }}</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.bought_date_and_rent_date="{ item }">
        <span
          v-text="
            item.bought_date != null
              ? dateFormat(item.bought_date)
              : dateFormat(item.rent_start_date) +
                ' / ' +
                dateFormat(item.rent_end_date)
          "
        ></span>
      </template>
      <template v-slot:item.recurrent="{ item }">
        <span v-text="item.recurrent ? $t('yes') : $t('No')"></span>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-4 grey lighten-5">
          <h4 class="mb-2">{{ $t('maintenanceHistory') }}</h4>
          <v-simple-table dense class="elevation-1">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">{{ $t('date') }}</th>
                  <th class="text-left">{{ $t('code') }}</th>
                  <th class="text-left">{{ $t('operator') }}</th>
                  <th class="text-left">{{ $t('mode') }}</th>
                  <th class="text-left">{{ $t('description') }}</th>
                  <th class="text-left">{{ $t('cost') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in item.maintenances" :key="m.pk">
                  <td>{{ dateFormat(m.start_date) }}</td>
                  <td>{{ m.code || 'N/A' }}</td>
                  <td>{{ m.operator }}</td>
                  <td>{{ m.mode }}</td>
                  <td>{{ m.description }}</td>
                  <td>{{ m.payment }} {{ m.pay }}</td>
                </tr>
                <tr v-if="!item.maintenances || item.maintenances.length === 0">
                  <td colspan="6" class="text-center italic grey--text">
                    {{ $t('noMaintenanceRecords') }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </td>
      </template>
      <template v-slot:extraToolbar>
        <v-divider class="mx-4" inset vertical></v-divider>
        <i-btn
          text
          :small="$vuetify.breakpoint.smAndDown"
          icon="fa-file-excel"
          :title="$t('download', { value: $t('equipmentReport') })"
          color="text-dark"
          @click="getEquipmentsExcel()"
        >
        </i-btn>
        <i-btn
          text
          :small="$vuetify.breakpoint.smAndDown"
          icon="fa-file-pdf"
          :title="$t('download', { value: $t('equipmentReport') })"
          color="text-dark"
          @click="getEquipmentsPDF()"
        >
        </i-btn>
      </template>
    </i-table>
  </v-container>
</template>
<script>
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      form: {
        name: '',
        production_department: '',
        model: '',
        recurrent: null
      },
      start: '',
      end: '',
      modelList: [],
      departmentList: [],
      expanded: [],
      recurrent_options: [
        { pk: null, name: this.$t('all') },
        { pk: false, name: this.$t('regular') },
        { pk: true, name: this.$t('recurrent') }
      ]
    }
  },
  mounted() {
    this.getModels()
    this.getDepartments()
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('name'),
          value: 'name'
        },
        {
          text: this.$t('model'),
          value: 'modelName'
        },
        {
          text: this.$t('serial'),
          value: 'serial'
        },
        {
          text: this.$t('mode'),
          value: 'mode'
        },
        {
          text: this.$t('bought_date_and_rent_date'),
          value: 'bought_date_and_rent_date'
        },
        {
          text: this.$t('department'),
          value: 'production_departament.name'
        },
        {
          text: this.$tc('recurrent', 0),
          value: 'recurrent'
        }
      ]
    },
    ...mapGetters({
      company: 'company/getCompanyData'
    })
  },
  methods: {
    getEquipmentsExcel() {
      this.downloadFile({
        api: 'equipment',
        method: 'excel',
        filename: 'Equipments',
        action: 'download',
        ext: 'xlsx',
        opt: {
          pk: this.company.pk,
          params:
            '?name=' +
            this.form.name +
            '&production_department=' +
            this.form.production_department +
            '&model=' +
            this.form.model +
            '&date_from=' +
            this.start +
            '&date_to=' +
            this.end +
            '&recurrent=' +
            this.form.recurrent
        }
      })
    },
    getEquipmentsPDF() {
      this.downloadFile({
        api: 'equipment',
        method: 'pdf',
        filename: 'Equipments',
        action: 'download',
        ext: 'pdf',
        opt: {
          pk: this.company.pk,
          params:
            '?name=' +
            this.form.name +
            '&production_department=' +
            this.form.production_department +
            '&model=' +
            this.form.model +
            '&date_from=' +
            this.start +
            '&date_to=' +
            this.end +
            '&recurrent=' +
            this.form.recurrent
        }
      })
    },
    getModels() {
      this.$api.equipment.brand
        .list({
          opt: {
            params: {
              isModel: true
            }
          }
        })
        .then((response) => {
          this.modelList = response.data.results
        })
    },
    getDepartments() {
      this.$api.department
        .list({
          opt: {
            params: {
              company_id: this.company.pk
            }
          }
        })
        .then((response) => {
          this.departmentList = response.data.results
        })
    },
    clear() {
      this.form = {
        name: '',
        production_department: '',
        model: '',
        recurrent: null
      }
      this.start = ''
      this.end = ''
    }
  }
}
</script>
